import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Button from './ui/Button';

interface VfxPlannerStubProps {
  isOpen?: boolean;
  onClose?: () => void;
  currentUserRole?: string;
}

interface VfxStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  totalArtists: number;
  availableArtists: number;
  apiStatus: 'online' | 'offline' | 'unknown';
  lastUpdate: string;
}

const VfxPlannerStub: React.FC<VfxPlannerStubProps> = ({ 
  isOpen = true, 
  onClose,
  currentUserRole 
}) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<VfxStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Fetch VFX Planner statistics
  useEffect(() => {
    if (isOpen) {
      fetchVfxStats();
    }
  }, [isOpen]);

  const fetchVfxStats = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/vfx-planner/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // API endpoint doesn't exist yet, use mock data
        setStats({
          totalProjects: 12,
          activeProjects: 8,
          completedProjects: 4,
          totalArtists: 45,
          availableArtists: 23,
          apiStatus: 'offline',
          lastUpdate: new Date().toISOString()
        });
      }
    } catch (err) {
      // Use mock data on error
      setStats({
        totalProjects: 12,
        activeProjects: 8,
        completedProjects: 4,
        totalArtists: 45,
        availableArtists: 23,
        apiStatus: 'offline',
        lastUpdate: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/vfx-planner/test`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setError('');
        await fetchVfxStats();
      } else {
        setError('VFX Planner API is not available yet');
      }
    } catch (err) {
      setError('Connection failed. VFX Planner API is not implemented.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mx-auto mb-4">
          <ICONS.Monitor className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          VFX Planner API
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Advanced project planning and resource management system
        </p>
      </div>

      {/* API Status */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            API Status
          </h3>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            stats?.apiStatus === 'online' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            {stats?.apiStatus === 'online' ? 'Online' : 'Offline'}
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          API URL: {API_CONFIG.API_URL}/vfx-planner
        </p>
        {stats?.lastUpdate && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Last update: {new Date(stats.lastUpdate).toLocaleString()}
          </p>
        )}
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.totalProjects}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              Total Projects
            </div>
          </div>
          
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.activeProjects}
            </div>
            <div className="text-sm text-green-700 dark:text-green-300">
              Active Projects
            </div>
          </div>
          
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {stats.totalArtists}
            </div>
            <div className="text-sm text-purple-700 dark:text-purple-300">
              Total Artists
            </div>
          </div>
          
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {stats.availableArtists}
            </div>
            <div className="text-sm text-orange-700 dark:text-orange-300">
              Available Artists
            </div>
          </div>
        </div>
      )}

      {/* API Endpoints */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          API Endpoints
        </h3>
        
        <div className="space-y-3">
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <ICONS.Database className="w-4 h-4 mr-2" />
              Statistics & Health
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">GET /api/v1/vfx-planner/stats</code>
                <span className="text-gray-500">Get system statistics</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">GET /api/v1/vfx-planner/health</code>
                <span className="text-gray-500">Health check</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <ICONS.Briefcase className="w-4 h-4 mr-2" />
              Project Management
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">GET /api/v1/vfx-planner/projects</code>
                <span className="text-gray-500">List all projects</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">POST /api/v1/vfx-planner/projects</code>
                <span className="text-gray-500">Create project</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">PUT /api/v1/vfx-planner/projects/:id</code>
                <span className="text-gray-500">Update project</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">DELETE /api/v1/vfx-planner/projects/:id</code>
                <span className="text-gray-500">Delete project</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <ICONS.Users className="w-4 h-4 mr-2" />
              Resource Management
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">GET /api/v1/vfx-planner/assignments</code>
                <span className="text-gray-500">Get artist assignments</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">POST /api/v1/vfx-planner/assignments</code>
                <span className="text-gray-500">Assign artist to project</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs">GET /api/v1/vfx-planner/availability</code>
                <span className="text-gray-500">Check artist availability</span>
              </div>
            </div>
          </div>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
            <h4 className="font-medium text-purple-900 dark:text-purple-100 mb-2 flex items-center">
              <ICONS.Link className="w-4 h-4 mr-2" />
              LightBox Integration (Active)
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs">POST /api/v1/magicbox/test-connection</code>
                <span className="text-purple-600 dark:text-purple-300">Test connection</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs">POST /api/v1/magicbox/connect/:projectId</code>
                <span className="text-purple-600 dark:text-purple-300">Connect project</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs">POST /api/v1/magicbox/sync/push/:biddingSheetId</code>
                <span className="text-purple-600 dark:text-purple-300">Push comments</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs">POST /api/v1/magicbox/sync/pull/:projectId</code>
                <span className="text-purple-600 dark:text-purple-300">Pull statuses</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs">GET /api/v1/magicbox/sync/status/:projectId</code>
                <span className="text-purple-600 dark:text-purple-300">Sync status</span>
              </div>
              <div className="flex items-center justify-between">
                <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs">GET /api/v1/magicbox/mapping/:projectId</code>
                <span className="text-purple-600 dark:text-purple-300">Shot mapping</span>
              </div>
            </div>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-3">
              ✓ Синхронизация комментариев и аннотаций SpellBook → LightBox<br/>
              ✓ Получение статусов шотов и прогресса LightBox → SpellBook
            </p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Planned Features
        </h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Project Management
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Timeline visualization</li>
              <li>• Resource allocation</li>
              <li>• Milestone tracking</li>
              <li>• Budget management</li>
            </ul>
          </div>
          
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Artist Management
            </h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Skill matching</li>
              <li>• Availability tracking</li>
              <li>• Workload balancing</li>
              <li>• Performance metrics</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Integration Instructions */}
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
        <div className="flex items-start">
          <ICONS.AlertTriangle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5" />
          <div>
            <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
              Integration Required
            </h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
              The VFX Planner API is not yet implemented. To enable this feature:
            </p>
            <ol className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1 list-decimal list-inside">
              <li>Implement backend API endpoints</li>
              <li>Create database schema for projects and assignments</li>
              <li>Add authentication and authorization</li>
              <li>Integrate with existing artist management system</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={handleTestConnection}
          disabled={loading}
          className="flex-1 flex items-center justify-center space-x-2"
        >
          <ICONS.RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Test Connection</span>
        </Button>
        
        <Button
          onClick={fetchVfxStats}
          disabled={loading}
          variant="secondary"
          className="flex-1 flex items-center justify-center space-x-2"
        >
          <ICONS.RefreshCw className="w-4 h-4" />
          <span>Refresh Stats</span>
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <ICONS.AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <ICONS.Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
        </div>
      )}
    </div>
  );
};

export default VfxPlannerStub;